import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * The active subpage of a state-toggled shell (member or admin), mirrored in
 * the URL as `?subpage=<id>` so email links such as `/dashboard?subpage=withdraw`
 * open the right page, and a reload keeps it. An unknown id falls back to
 * `fallback`; the fallback page itself is shown without the query. The URL is
 * updated with `replace`, so switching pages does not fill the history.
 */
const useSubpageParam = (validIds, fallback = 'dashboard') => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('subpage');
  const pick = (id) => (id && validIds.includes(id) ? id : fallback);
  const [active, setActiveState] = useState(() => pick(requested));

  // A link followed while the shell is already open (same tab) changes the
  // query without remounting: follow it.
  useEffect(() => {
    if (requested && validIds.includes(requested)) setActiveState(requested);
  }, [requested, validIds]);

  const setActive = useCallback(
    (id) => {
      setActiveState(id);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (!id || id === fallback || !validIds.includes(id)) next.delete('subpage');
          else next.set('subpage', id);
          return next;
        },
        { replace: true },
      );
    },
    [fallback, setSearchParams, validIds],
  );

  return [active, setActive];
};

export default useSubpageParam;
